import { useTranslation } from "react-i18next";
import { Dropdown, Icon } from "semantic-ui-react";

import { DealersAndLocationsDropdown, ResetFiltersButton, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import "modules/ManufacturerDashboard/components/ManufacturerDashboardFilters/ManufacturerDashboardFilters.scss";
import {
  setManufacturerDashboardFilters,
  useManufacturerDashboardFilters,
  useManufacturerDropdownOptions,
  useManufacturerFilterHandlers
} from "modules/ManufacturerDashboard/hooks";
import { MANUFACTURER_STORAGE_KEYS } from "modules/ManufacturerDashboard/storageKeys";
import { setPreference } from "utils/common";

import type { PIN_STATUSES } from "models";

type ManufacturerDashboardFilterProps = {
  onOpenRequestModal: () => void;
};

export const ManufacturerDashboardFilter = ({ onOpenRequestModal }: ManufacturerDashboardFilterProps) => {
  const { t } = useTranslation();

  const canCreateRequest = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.ManufacturerRequest);

  const { handleResetFilters, handleDealerLocationChange } = useManufacturerFilterHandlers();
  const filters = useManufacturerDashboardFilters();
  const { location_ids: locationIds, dealer_ids: dealerIds, pin_status_id, mechanic_fixed } = filters;
  const { pinStatusDropdownOptions, mechanicFixedDropdownOptions } = useManufacturerDropdownOptions();

  const numOfColumns = canCreateRequest ? 5 : 4;

  return (
    <div className={`Manufacturer__filters-container Manufacturer__filters-container-${numOfColumns}`}>
      {canCreateRequest && (
        <div onClick={onOpenRequestModal} className="Manufacturer__filters-container--addButton">
          <Icon className="plus" />
          <span>{t("v8_add", "Add")}</span>
        </div>
      )}
      <DealersAndLocationsDropdown
        allowSelectAllDealers={false}
        values={{ dealerIds, locationIds }}
        handleOnChange={({ dealerIds, locationIds }) => handleDealerLocationChange({ dealerIds, locationIds })}
      />
      <Dropdown
        options={pinStatusDropdownOptions}
        placeholder={t("v8_pin_status", "Pin status")}
        multiple
        fluid
        clearable
        selection
        selectOnBlur={false}
        value={pin_status_id || []}
        onChange={(_, { value }) => {
          setManufacturerDashboardFilters({ ...filters, pin_status_id: value as PIN_STATUSES[] });
          setPreference(MANUFACTURER_STORAGE_KEYS.PinStatusPreference, value);
        }}
      />
      <Dropdown
        options={mechanicFixedDropdownOptions}
        value={mechanic_fixed ?? ""}
        fluid
        clearable
        selection
        selectOnBlur={false}
        placeholder={t("v8_mechanic_fixed", "Mechanic Fixed")}
        onChange={(_, { value }) => {
          const updatedValue = typeof value === "string" ? null : !!value;
          setManufacturerDashboardFilters({ ...filters, mechanic_fixed: updatedValue });
          setPreference(MANUFACTURER_STORAGE_KEYS.MechanicFixedPreference, updatedValue);
        }}
      />
      <ResetFiltersButton handleReset={handleResetFilters} />
    </div>
  );
};
